import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from '@/shared/lib';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import type { NetworkOption, WithdrawCryptoFormValues } from '../model/types';
import type { UseFormReturn } from 'react-hook-form';
import { useCryptoAddressCheck } from '@/entities/crypto-address';

interface UseAddressCheckDataParams {
  address: string;
  selectedNetwork?: NetworkOption;
  assetCode: string;
  form: UseFormReturn<WithdrawCryptoFormValues>;
}

export function useAddressCheckData({
  address,
  selectedNetwork,
  assetCode,
  form,
}: UseAddressCheckDataParams) {
  const [checkAddressResult, setCheckAddressResult] = useState('');
  const [invalidMessageResult, setInvalidMessageResult] = useState('');
  const [manualAddressError, setManualAddressError] = useState<unknown | null>(
    null
  );

  const cryptoAddressFieldState = form.getFieldState('cryptoAddress');
  const hasTouchedAddress = cryptoAddressFieldState.isTouched || !!address;
  const debouncedAddress = useDebounce(address, COMMISSION_CHECK_DELAY);
  const checkAddressEnabled = !!(
    debouncedAddress &&
    selectedNetwork?.name &&
    assetCode
  );

  const { checkAddressData, checkingAddressError, isAddressCheckFetching } =
    useCryptoAddressCheck({
      network: selectedNetwork?.name || '',
      address: debouncedAddress,
      enabled: checkAddressEnabled,
    });

  const isStillTyping = debouncedAddress !== address;
  const isValidatingAddress = useMemo(() => {
    const hasValueAndNetwork = address && selectedNetwork?.name;
    return (
      hasTouchedAddress &&
      !!hasValueAndNetwork &&
      (isAddressCheckFetching || isStillTyping)
    );
  }, [
    address,
    debouncedAddress,
    selectedNetwork?.name,
    isAddressCheckFetching,
    hasTouchedAddress,
    isStillTyping,
  ]);

  useEffect(() => {
    if (checkAddressData) {
      setCheckAddressResult(checkAddressData.result);
      setInvalidMessageResult(checkAddressData.resultInvalidMessage);
    }
  }, [checkAddressData]);

  useEffect(() => {
    if (checkAddressResult) {
      form.trigger('cryptoAddress');
    }
  }, [checkAddressResult, form]);

  useEffect(() => {
    if (selectedNetwork && address) {
      setCheckAddressResult('');
    }
  }, [selectedNetwork?.code, address]);

  useEffect(() => {
    setManualAddressError(null);
    form.clearErrors('cryptoAddress');
  }, [address, assetCode, selectedNetwork?.code]);

  return {
    checkAddressData,
    checkingAddressError,
    isAddressCheckFetching,
    isValidatingAddress,
    checkAddressResult,
    invalidMessageResult,
    manualAddressError,
    setManualAddressError,
    debouncedAddress,
  };
}
